import { useCallback, useState } from 'react';
import type { HouseholdInput } from '@/types';
import { householdEquals } from '@/utils/householdEquals';

type SubmitResult = { success: boolean; error?: string };

type Params = {
  user: { id: number } | null | undefined;
  household: HouseholdInput | null;
  isLoadingHousehold: boolean;
  saveHousehold: (input: HouseholdInput) => Promise<void>;
  submit: (input: HouseholdInput) => Promise<SubmitResult>;
};

type Return = {
  showConfirmModal: boolean;
  pendingSubmit: HouseholdInput | null;
  handleSubmit: (payload: HouseholdInput) => Promise<SubmitResult | undefined>;
  handleConfirmUpdate: () => Promise<SubmitResult | undefined>;
  handleCancelUpdate: () => Promise<SubmitResult | undefined>;
};

export function useSaveWithConfirm({ user, household, isLoadingHousehold, saveHousehold, submit }: Params): Return {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<HouseholdInput | null>(null);

  const handleSubmit = useCallback(async (payload: HouseholdInput): Promise<SubmitResult | undefined> => {
    // Guard: Don't make decisions about household data while it's still loading
    if (isLoadingHousehold) {
      return undefined;
    }

    if (user && !household) {
      // Save new household and proceed
      saveHousehold(payload).catch(() => {/* surface handled upstream if needed */});
      return submit(payload);
    } else if (user && household) {
      const hasChanged = !householdEquals(household, payload);
      if (hasChanged) {
        setPendingSubmit(payload);
        setShowConfirmModal(true);
        return undefined; // Will be submitted via confirm/cancel handlers
      } else {
        return submit(payload);
      }
    } else {
      // Not logged in, just calculate
      return submit(payload);
    }
  }, [user, household, isLoadingHousehold, saveHousehold, submit]);

  const handleConfirmUpdate = useCallback(async (): Promise<SubmitResult | undefined> => {
    if (!pendingSubmit) return undefined;
    try {
      await saveHousehold(pendingSubmit);
      return submit(pendingSubmit);
    } finally {
      setPendingSubmit(null);
      setShowConfirmModal(false);
    }
  }, [pendingSubmit, saveHousehold, submit]);

  const handleCancelUpdate = useCallback(async (): Promise<SubmitResult | undefined> => {
    if (!pendingSubmit) {
      return undefined;
    }
    const result = await submit(pendingSubmit);
    setPendingSubmit(null);
    setShowConfirmModal(false);
    return result;
  }, [pendingSubmit, submit]);

  return {
    showConfirmModal,
    pendingSubmit,
    handleSubmit,
    handleConfirmUpdate,
    handleCancelUpdate,
  } as const;
}
